import { useCallback, useEffect, useId, useRef, useState } from "react";
import usePhoneLayout from "../hooks/usePhoneLayout";
import { useTranslation } from "react-i18next";
import { LuSlidersHorizontal, LuX } from "react-icons/lu";

const MOBILE = "(max-width: 767px)";
const isMobile = () => Boolean(window.matchMedia?.(MOBILE).matches);

// Below 768 px the existing filter form opens in a bottom-sheet behind a "Filtres" button.
// From 768 px the children render inline exactly as before (trigger and sheet chrome are hidden by CSS).
// The sheet closes on form submit or on a click on an element marked data-filter-apply.
export default function MobileFilterSheet({ children, summary, firstLevel }) {
  const phone = usePhoneLayout();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  const trigger = useRef(null), sheet = useRef(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const root = document.documentElement, body = document.body;
    const previous = root.style.overflow;
    if (isMobile()) root.style.overflow = "hidden";
    body.classList.add("lina-m-sheet-open");
    sheet.current?.focus({ preventScroll: true });
    const onKey = event => { if (event.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    const toggle = trigger.current;
    return () => {
      document.removeEventListener("keydown", onKey);
      root.style.overflow = previous;
      body.classList.remove("lina-m-sheet-open");
      toggle?.focus({ preventScroll: true });
    };
  }, [open, close]);

  // Growing past the mobile breakpoint always returns to the inline form.
  useEffect(() => {
    const query = window.matchMedia?.(MOBILE);
    if (!query?.addEventListener) return undefined;
    const onChange = event => { if (!event.matches) setOpen(false); };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return (
    <div
      className="lina-m-filters"
      data-open={open || undefined}
      onClick={event => { if (open && event.target.closest?.("[data-filter-apply]")) close(); }}
      onSubmit={() => { if (open) close(); }}
    >
      {phone && summary && <div className="phone-filter-summary">{summary}</div>}
      <button ref={trigger} type="button" className="lina-m-filters-trigger"
        aria-expanded={open || expanded} aria-controls={id} onClick={() => { if (phone && firstLevel && !expanded) setExpanded(true); else setOpen(true); }}>
        <LuSlidersHorizontal aria-hidden="true" size={19} />
        {t("mobile.filters")}
      </button>
      {phone && firstLevel && expanded && <div className="phone-filter-first">{firstLevel}</div>}
      {open && <div className="lina-m-filters-backdrop" aria-hidden="true" onClick={close} />}
      <div id={id} ref={sheet} tabIndex={-1} className="lina-m-filters-body"
        role={open ? "dialog" : undefined} aria-modal={open || undefined}
        aria-label={open ? t("mobile.filters") : undefined}>
        {open && (
          <div className="lina-m-filters-head">
            <strong>{t("mobile.filters")}</strong>
            <button type="button" onClick={close} aria-label={t("mobile.closeFilters")} title={t("mobile.closeFilters")}>
              <LuX aria-hidden="true" size={20} />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
